'use client'

import type { ReactNode } from 'react'
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'

import { useHydrated } from '@/hooks/use-hydrated'
import { useIsHydratingRender } from '@/hooks/use-hydrating-render'
import { cn } from '@/lib/utils'
import { GatedTooltipTrigger, TooltipSsrGateProvider, useTooltipSsrGate } from './tooltip-ssr-gate'

function TooltipProvider({ delay = 0, ...props }: TooltipPrimitive.Provider.Props) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
  // See ./tooltip-ssr-gate.tsx. On the server — and on the client render that hydrates it — the whole Base
  // UI tooltip is replaced by the one element it would have emitted; the real one mounts in the re-render
  // straight after hydration. A tooltip asked to start OPEN has real popup content in the server HTML, so
  // it is never gated.
  // Both flags, and neither is redundant: `useHydrated()` lands the swap in the app-wide re-render the
  // root effect causes (`ButtonPrimitive` gates on it too), `useIsHydratingRender()` states the hard
  // requirement per component so a LATE Suspense boundary cannot render the real tree into lite HTML.
  const hydrating = useIsHydratingRender()
  const hydrated = useHydrated()
  // A render-function child is Base UI's payload API — it needs the real root to be called at all.
  const gateable = !props.open && !props.defaultOpen && typeof props.children !== 'function'
  if ((hydrating || !hydrated) && gateable) {
    return <TooltipSsrGateProvider value={true}>{props.children as ReactNode}</TooltipSsrGateProvider>
  }

  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  if (useTooltipSsrGate()) return <GatedTooltipTrigger {...props} />

  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  side = 'top',
  sideOffset = 8,
  align = 'center',
  alignOffset = 0,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<TooltipPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'>) {
  // Portalled, so it emits nothing on the server anyway — but under the gate there is no Base UI root to
  // read from, and rendering it would throw.
  if (useTooltipSsrGate()) return null

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50"
      >
        <TooltipPrimitive.Popup
          // Base UI renders the popup as a bare <div> with no ARIA role and expects the trigger to carry an
          // `aria-label` instead (mui/base-ui#1420, #1427, closed as expected); the role is opt-in in userland.
          // NOT wired via aria-describedby, so screen-reader announcement still relies on the trigger label.
          role="tooltip"
          data-slot="tooltip-content"
          className={cn(
            'z-50 inline-flex w-fit max-w-xs origin-(--transform-origin) items-center gap-1.5 rounded-md border border-border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md has-data-[slot=kbd]:pr-1.5 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className,
          )}
          {...props}
        >
          {children}
          {/* A 45°-rotated square: the bordered pair is the two edges meeting at the corner facing the anchor, the -4px inset hides the rest behind the popup, and the cross-axis offset stays Base UI's inline one (it centres the arrow on the trigger). */}
          <TooltipPrimitive.Arrow className="border-border bg-popover z-50 size-2.5 rotate-45 rounded-[2px] data-[side=bottom]:-top-1 data-[side=bottom]:border-t data-[side=bottom]:border-l data-[side=inline-end]:-left-1 data-[side=inline-end]:border-b data-[side=inline-end]:border-l data-[side=inline-start]:-right-1 data-[side=inline-start]:border-t data-[side=inline-start]:border-r data-[side=left]:-right-1 data-[side=left]:border-t data-[side=left]:border-r data-[side=right]:-left-1 data-[side=right]:border-b data-[side=right]:border-l data-[side=top]:-bottom-1 data-[side=top]:border-r data-[side=top]:border-b" />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
